import React from 'react';
import PropTypes from 'prop-types';
// @mui
import {
  Card,
  CardContent,
  Typography,
  Stack,
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
// @iconify
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import editFill from '@iconify/icons-eva/edit-fill';
import trash2Fill from '@iconify/icons-eva/trash-2-fill';
import creditCardOutline from '@iconify/icons-eva/credit-card-outline';
import smartphoneFill from '@iconify/icons-eva/smartphone-fill';
import personFill from '@iconify/icons-eva/person-fill';

// constants
import { statusColorMap, Status } from '../constants/ocpi.constant';

const StyledCard = styled(Card)(({ theme, selected, isValid }) => ({
  border: `2px solid ${selected ? theme.palette.success.main : 'transparent'}`,
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: selected ? theme.shadows[8] : theme.shadows[2],
  transition: 'all 0.3s ease',
  background: selected ? 'linear-gradient(135deg, #e8f5e8 0%, #f0f9f0 100%)' : 'white',
  '&:hover': {
    boxShadow: theme.shadows[8],
    transform: 'translateY(-2px)'
  },
  height: '100%',
  display: 'flex',
  flexDirection: 'column'
}));

const TokenIcon = ({ tokenType }) => {
  const getIcon = () => {
    switch (tokenType) {
      case 'RFID':
        return creditCardOutline;
      case 'MOBILE':
        return smartphoneFill;
      case 'AD_HOC_USER':
        return personFill;
      default:
        return creditCardOutline;
    }
  };

  return <Icon icon={getIcon()} width={20} height={20} />;
};

TokenIcon.propTypes = {
  tokenType: PropTypes.string.isRequired
};

const TokenCard = ({ token, selected, onSelect, onView, onEdit, onDelete }) => {
  // Map OCPI token data to display format
  const displayData = {
    id: token.id || token.uid,
    tokenUid: token.uid || token.token_uid || 'N/A',
    tokenType: token.type || token.token_type || 'RFID',
    countryCode: token.country_code || token.partner_country_code || 'N/A',
    partyId: token.party_id || token.partner_party_id || 'N/A',
    contractId: token.contract_id || 'N/A',
    issuer: token.issuer || token.partner_name || 'N/A',
    profile: token.default_profile_type || 'N/A',
    visualNumber: token.visual_number || 'N/A',
    isValid: token.valid !== undefined ? token.valid : true,
    status: token.valid ? Status.VALID : Status.INVALID,
    lastUpdated: token.last_updated || token.updated_at || new Date().toISOString()
  };

  return (
    <StyledCard selected={selected} isValid={displayData.isValid}>
      <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header with Checkbox and Status */}
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Checkbox
              checked={selected}
              onChange={() => onSelect(displayData.id)}
              color="success"
              size="small"
            />
            <Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <TokenIcon tokenType={displayData.tokenType} />
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.2 }}>
                  {displayData.tokenUid}
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                {new Date(displayData.lastUpdated).toLocaleDateString()}
              </Typography>
            </Stack>
          </Stack>
          <Chip
            label={displayData.isValid ? Status.VALID : Status.INVALID}
            color={statusColorMap[displayData.status].color}
            size="small"
            variant="filled"
            sx={{ 
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 24,
              bgcolor: statusColorMap[displayData.status].bgcolor,
              color: statusColorMap[displayData.status].text,
            }}
          />
        </Stack>

        {/* Main Content Grid */}
        <Box sx={{ mb: 2, flex: 1 }}>
          <Stack direction="row" spacing={3}>
            {/* Left Column */}
            <Box sx={{ flex: 1 }}>
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                  COUNTRY / PARTY
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                  {displayData.countryCode} - {displayData.partyId}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                  TYPE
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={displayData.tokenType}
                    variant="outlined"
                    size="small"
                    color="primary"
                    sx={{ fontSize: '0.7rem', height: 22 }}
                  />
                </Box>
              </Box>
              
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                  CONTRACT ID
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600, 
                    fontSize: '0.85rem',
                    wordBreak: 'break-all',
                    lineHeight: 1.3
                  }}
                >
                  {displayData.contractId}
                </Typography>
              </Box>
            </Box>

            {/* Right Column */}
            <Box sx={{ flex: 1 }}>
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                  ISSUER
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                  {displayData.issuer}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                  PROFILE
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={displayData.profile}
                    variant="outlined"
                    size="small"
                    color="secondary"
                    sx={{ fontSize: '0.7rem', height: 22 }}
                  />
                </Box>
              </Box>
              
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                  VISUAL NUMBER
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                  {displayData.visualNumber}
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Action Buttons */}
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            size="small"
            variant="outlined"
            startIcon={<Icon icon={eyeFill} width={16} height={16} />}
            onClick={() => onView(displayData.id)}
            sx={{
              fontSize: '0.75rem',
              px: 1.5,
              py: 0.5,
              minWidth: 'auto',
              height: 32,
              borderColor: 'grey.300',
              color: 'text.primary',
              '&:hover': { 
                borderColor: 'grey.400',
                backgroundColor: 'grey.50'
              }
            }}
          >
            View
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Icon icon={editFill} width={16} height={16} />}
            onClick={() => onEdit(displayData.id)}
            sx={{
              fontSize: '0.75rem',
              px: 1.5,
              py: 0.5,
              minWidth: 'auto',
              height: 32,
              borderColor: 'grey.300',
              color: 'text.primary',
              '&:hover': { 
                borderColor: 'grey.400',
                backgroundColor: 'grey.50'
              }
            }}
          >
            Edit
          </Button>
          <IconButton
            size="small"
            onClick={() => onDelete(displayData.id)}
            sx={{
              width: 32,
              height: 32,
              color: 'error.main',
              '&:hover': { 
                backgroundColor: 'error.lighter'
              }
            }}
          >
            <Icon icon={trash2Fill} width={16} height={16} />
          </IconButton>
        </Stack>
      </CardContent>
    </StyledCard>
  );
};

export default TokenCard;
